import { ExportDropdown } from "@components/common/export/export-dropdown";
import { useDateTime } from "@hooks/use-date-time";
import { useMembers } from "@hooks/use-members";
import {
  creditTransactionsSelector,
  fetchingTransactionsFlagsSelector,
} from "@store/credit-transactions/credit-transactions-selector";
import { fetchedMembersFlagsSelector } from "@store/members/members-selector";
import { projectsSelector } from "@store/projects/projects-selector";
import {
  allCreditSubscriptionsSelector,
  selectedSdbCompanyNameSelector,
} from "@store/sdb-company/sdb-company-selector";
import { useAppSelector } from "@store/store-helper";
import {
  downloadPurchasesData,
  downloadTransactionsData,
  ExportFormat,
} from "@utils/file-data-export";
import { TableType } from "@utils/track-event/track-event-list";

interface Props {
  /** Whether the current toggleView is transactions */
  isTransactionsView: boolean;
}

/** Dropdown to export the transactions or purchases in the file format selected by the user */
export function ExportTransactionsOrPurchases({
  isTransactionsView,
}: Props): JSX.Element {
  const { formatDate } = useDateTime();
  const companyName = useAppSelector(selectedSdbCompanyNameSelector);
  const { companyMembers } = useMembers();
  const transactions = useAppSelector(creditTransactionsSelector);
  const projects = useAppSelector(projectsSelector);
  const { status } = useAppSelector(fetchingTransactionsFlagsSelector);
  const { hasFetchedWorkspaceMembers } = useAppSelector(
    fetchedMembersFlagsSelector
  );
  const subscriptions = useAppSelector(allCreditSubscriptionsSelector);

  const isLoadingTransactions =
    isTransactionsView &&
    (!hasFetchedWorkspaceMembers || status === "pending");

  /** Export handler for transactions or purchases based on the current view */
  async function exportHandler(format: ExportFormat): Promise<number> {
    if (isTransactionsView) {
     return await downloadTransactionsData({
        fileName: `${companyName}-Transactions List`,
        transactions,
        companyMembers,
        projects,
        formatDate,
        format,
      });
    } else {
      return await downloadPurchasesData({
        subscriptions,
        fileName: `${companyName}-Purchases List`,
        formatDate,
        format,
      });
    }
  }

  return (
    <ExportDropdown
      trackingProps={{
        dataType: isTransactionsView
          ? TableType.creditTransactions
          : TableType.creditPurchases,
        numberOfEntities: isTransactionsView
          ? transactions.length
          : subscriptions.length,
        areEntitiesSelected: false,
      }}
      exportHandler={exportHandler}
      toastMessage={{
        success: `Successfully exported ${
          isTransactionsView ? "transactions" : "purchases"
        }.`,
        error: `Couldn't export ${
          isTransactionsView ? "transactions" : "purchases"
        }. Please try again.`,
      }}
      buttonTooltipText={`Export ${
        isTransactionsView ? "transactions" : "purchases"
      }`}
      shouldShowSkeleton={isLoadingTransactions}
      buttonType="standard"
    />
  );
}
